import { DatePipe, NgFor, NgIf } from '@angular/common';
import { Component, Directive, Inject, Injectable, Input } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import {
  DateRange,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDateRangeSelectionStrategy,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Site } from '@reenstudio/app/core/models/site/site';
import { UserRole } from '@reenstudio/app/core/enum/user-role';
import { EventService } from '@reenstudio/app/core/services/event-service';
import { NavService } from '@reenstudio/app/core/services/nav.service';
import { StorageService } from '@reenstudio/app/core/services/storage.service';
import { Subscription } from 'rxjs';
import { SiteApiService } from '@reenstudio/app/core/services/api/site.services';
import { NGXLogger } from 'ngx-logger';
import { FilterConfig, filterConfigs } from './filter-map';

@Injectable()
export class MaxRangeSelectionStrategy<D>
  implements MatDateRangeSelectionStrategy<D>
{
  start: any;
  public delta: number;
  constructor(private _dateAdapter: DateAdapter<D>) {}

  selectionFinished(date: D, currentRange: DateRange<D>) {
    let { start, end } = currentRange;
    if (start == null || (start && end)) {
      start = date;
      end = null;
    } else if (end == null) {
      const maxDate = this._dateAdapter.addCalendarDays(start, this.delta);
      end = date ? (date > maxDate ? maxDate : date) : null;
    }

    return new DateRange<D>(start, end);
  }
  createPreview(
    activeDate: D | null,
    currentRange: DateRange<D>
  ): DateRange<D> {
    if (currentRange.start && !currentRange.end) {
      const maxDate = this._dateAdapter.addCalendarDays(
        currentRange.start,
        this.delta
      );
      const rangeEnd = activeDate
        ? activeDate > maxDate
          ? maxDate
          : activeDate
        : null;

      return new DateRange(currentRange.start, rangeEnd);
    }

    return new DateRange<D>(null, null);
  }
}

@Directive({
  selector: '[maxRange]',
  standalone: true,
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: MaxRangeSelectionStrategy,
    },
  ],
})
export class MaxRangeDirective {
  constructor(
    @Inject(MAT_DATE_RANGE_SELECTION_STRATEGY)
    private maxRangeStrategy: MaxRangeSelectionStrategy<any>
  ) {}
  @Input() set maxRange(value: any) {
    this.maxRangeStrategy.delta = +Number.parseInt(value) || 7;
  }
}

@Component({
  selector: 'app-page-filters',
  templateUrl: './page-filters.component.html',
  styleUrls: ['./page-filters.component.scss'],
  standalone: true,
  imports: [
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    NgIf,
    NgFor,
    DatePipe,
    MaxRangeDirective,
  ],
})
export class PageFiltersComponent {
  // Varibile di input che cambia in base alla rotta della pagina
  @Input() type: string = '/history';
  maxDate: Date = new Date();
  selectedPeriodType: string;

  // Valori per select "Filtra per"
  selectedPeriodValue: string;
  historyPeriods: any = [
    { value: 'days', label: 'Giorni' },
    { value: 'months', label: 'Mesi' },
    { value: 'custom', label: 'Data personalizzata' },
  ];

  predictivePeriods: any = [
    { value: 'days', label: 'Giorni' },
    { value: 'months', label: 'Mesi' },
  ];

  // Valori storici per select "Seleziona periodo"
  historyOptions: any = [
    {
      name: 'Ultime 24 ore',
      value: '1',
    },
    {
      name: 'Ultimi 7 giorni',
      value: '7',
    },
    {
      name: 'Ultimi 15 giorni',
      value: '15',
    },
  ];

  // Valori previsionali per select "Seleziona periodo"
  predictiveOptions: any = [
    {
      name: 'Prossimi 7 giorni',
      value: '7',
    },
    {
      name: 'Prossimi 15 giorni',
      value: '15',
    },
  ];

  // Valori per seleziona Site
  selectedSite: string;
  sites: Site[] | null;

  //Variabili per select anni e mese
  years: any[];
  currentYear = new Date().getFullYear();

  currentMonth: number;
  months: any[] = this.getMonths();

  // Variabili per i forms
  filterForm: FormGroup;
  customRange: FormGroup;

  // Variabile per triggerare il resetForm
  formResetSubscription: Subscription;
  currentUrlSubscription: Subscription;

  userLevel: UserRole | null;
  filterConfigs: FilterConfig[] = [];
  currentUserLevel: string | null;

  constructor(
    private storageService: StorageService,
    private navService: NavService,
    private siteApiService: SiteApiService,
    private logger: NGXLogger
  ) {
    this.userLevel = this.storageService.getLevel();

    this.currentUrlSubscription = this.navService.currentUrl.subscribe(
      (url: string) => {
        this.type = url;
        this.filterConfigs = filterConfigs[this.type];
        this.formInit();
      }
    );

    // Inizializzo i forms ai valori di default
    this.formInit();

    // Inizializzo l'evento per il resetForm
    this.formResetSubscription = EventService.resetForm.subscribe(() => {
      this.filterForm.reset();
      this.customRange.reset();
      this.currentUrlSubscription = this.navService.currentUrl.subscribe(
        (url: string) => {
          this.type = url;
          this.formInit();
        }
      );
    });
  }

  formInit() {
    this.selectedPeriodType = 'days';

    this.selectedPeriodValue = this.type === '/history' ? '1' : '7';
    this.currentMonth = new Date().getMonth() + 1; //January is 0!
    this.currentYear = new Date().getFullYear();

    this.getCurrentUserLevel();

    let siteSelected = '';
    if (this.userLevel === UserRole.SITE) {
      const site = this.storageService.getSite();
      if (site !== null) {
        siteSelected = site.toString();
      } else {
        siteSelected = '1';
      }
    }

    this.sites = this.storageService.getSites();

    this.selectedSite =
      this.userLevel === UserRole.SITE ? siteSelected : 'total';

    this.filterForm = new FormGroup({
      periodType: new FormControl(this.selectedPeriodType),
      periodValue: new FormControl(this.selectedPeriodValue),
      monthList: new FormControl(this.currentMonth),
      yearsList: new FormControl(this.currentYear),
      site: new FormControl(this.selectedSite),
      customStartDate: new FormControl(),
      customEndDate: new FormControl(),
      textSearch: new FormControl(),
    });

    this.customRange = new FormGroup({
      start: new FormControl(),
      end: new FormControl(),
    });
  }

  getYears() {
    this.years = [];
    for (let i = 0; i < 5; i++) {
      this.years.push(this.currentYear - i);
    }
    return this.years;
  }

  getMonths() {
    let months = [];
    for (let i = 1; i <= 12; i++) {
      months.push({ label: this.currentYear + '-' + i + '-01', value: i });
    }
    return months;
  }

  changePeriodType(periodType: any) {
    this.selectedPeriodType = periodType;
  }

  collectCustomPeriod() {
    let endDate = new Date(this.customRange.value.end);
    endDate.setHours(23, 59, 59);

    this.filterForm.patchValue({
      customStartDate: this.customRange.value.start,
      customEndDate: endDate,
    });
  }

  filterCheck(filter: FilterConfig): boolean {
    let result: boolean;
    if (this.currentUserLevel) {
      result = filter.show && filter.userLevel.includes(this.currentUserLevel);
    } else {
      result = filter.show;
    }
    return result;
  }

  getCurrentUserLevel() {
    this.currentUserLevel = this.storageService.getLevel();
  }

  onSubmit() {
    if (typeof this.filterForm.value.site === 'number') {
      this.storageService.setSite(this.filterForm.value.site);
    }
    EventService.pageFilters.emit(this.filterForm.value);
  }

  ngOnDestroy() {
    this.formResetSubscription.unsubscribe();
    this.currentUrlSubscription.unsubscribe();
  }
}
